import { __decorate } from "tslib";
import { Injectable, inject } from '@angular/core';
import { Observable, map, concat, ignoreElements } from 'rxjs';
import { SailsSocketService } from 'src/ng-app/modules/sails-socket';
import { Schedule } from 'src/ng-app/modules/scheduling/schedule';
let AccessListService = class AccessListService {
    sails = inject(SailsSocketService);
    accessLists = new Map();
    _model = { array: [], byId: {}, byOrg: {} };
    get model() {
        return this._model;
    }
    /**
     * Fetches all access lists and updates the model.
     *
     * @returns {Observable<AccessListsModel>} Observable of the model.
     */
    getAccessLists() {
        return this.createObservable('/gateuserlist').pipe(map(({ data }) => {
            data.forEach((list) => {
                list.schedules = (list.schedules || [])
                    .map(sched => new Schedule(sched));
                list.exclusionSchedules = (list.exclusionSchedules || [])
                    .map(sched => new Schedule(sched));
            });
            this.updateModel(data);
            return this.model;
        }));
    }
    /**
     * Returns access lists for a given org, fetching if not cached.
     *
     * @param {string} org Organization ID
     * @returns {Observable<AccessList[]>} Observable of AccessList[]
     */
    getAccessListsForOrg(org) {
        const byOrg$ = new Observable((subscriber) => {
            subscriber.next(this._model.byOrg[org] || []);
            subscriber.complete();
        });
        const retrieveLists$ = this.accessLists.size
            ? new Observable((subscriber) => { subscriber.complete(); }).pipe(ignoreElements())
            : this.getAccessLists().pipe(ignoreElements());
        return concat(retrieveLists$, byOrg$);
    }
    createList(list) {
        return this.createObservable('/gateuserlist', 'post', list).pipe(map(res => res.data));
    }
    removeList(id) {
        return this.createObservable(`/gateuserlist/${id}`, 'delete').pipe(map(res => res.data));
    }
    updateList(id, updates) {
        return this.createObservable(`/gateuserlist/${id}`, 'put', updates).pipe(map(res => res.data));
    }
    /**
     * Save equipment assignments for an access list.
     *
     * @param {string} lid Access list ID
     * @param {Equipment['id'][]} equipmentIds Array of Equipment IDs
     * @returns {Observable<AccessList>} Observable of updated AccessList
     */
    saveGates(lid, equipmentIds) {
        return this.createObservable(`/gateuserlist/${lid}`, 'put', { equipment: equipmentIds }).pipe(map(res => res.data));
    }
    addUser(id, user) {
        return this.createObservable(`/gateuserlist/${id}/addUser`, 'post', user).pipe(map(res => res.data));
    }
    deleteUser(id, lid) {
        return this.createObservable(`/gateuserlist/${lid}/removeuser/${id}`, 'delete').pipe(map(res => res.data));
    }
    deleteUsers(ids, lid) {
        return this.createObservable(`/gateuserlist/${lid}/removeusers/${ids.join('|')}`, 'delete').pipe(map(res => res.data));
    }
    updateUser(id, lid, user) {
        return this.createObservable(`/gateuserlist/${lid}/updateuser/${id}`, 'put', user).pipe(map(res => res.data));
    }
    updateModel(data) {
        this.accessLists.clear();
        this._model.array = [];
        this._model.byId = {};
        this._model.byOrg = {};
        data.forEach((list) => {
            this.accessLists.set(list.id, list);
            this._model.array.push(list);
            this._model.byId[list.id] = list;
            this._model.byOrg[list.org] = this._model.byOrg[list.org] || [];
            this._model.byOrg[list.org].push(list);
        });
    }
    createObservable(url, method = 'get', data) {
        return new Observable((subscriber) => {
            const subscription = this.sails[method](url, data).subscribe({
                next: (response) => {
                    subscriber.next(response);
                    subscriber.complete();
                },
                error: (error) => subscriber.error(error),
            });
            return () => subscription.unsubscribe();
        });
    }
};
AccessListService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], AccessListService);
export { AccessListService };
